import cx from "classnames";
import { CustomDraftEditor } from "common/CustomDraftEditor";
import { EditorState } from "draft-js";
import { getVisibleSelectionRectFixed } from "lib/draftUtils";
import _ from "lodash";
import React from "react";
import { findDOMNode } from "react-dom";
import { InlineToolbar } from "./InlineToolbar";
import styles from "./RichTextEditor.scss";

// rich text editor component, encapsulates the logic for the inline toolbar
// and composes a CustomDraftEditor instance
export class RichTextEditor extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      inlineToolbarPos: { left: 0, top: 0 },
      frozenSelection: null,
    };
  }

  componentDidUpdate(prevProps) {
    this.setInlineToolbarPosition();
  }

  setInlineToolbarPosition() {
    const selectionPosition = getVisibleSelectionRectFixed(window);
    const anchorNode = document.getSelection().anchorNode;
    // Firefox throws errors on us even trying to access the nodeType property in
    // <input> elements, since the input area is implemented as an 'anonymous div'
    //
    // the only workaround seems to be to wrap this in a try/catch block
    // https://github.com/facebook/react/blob/master/src/renderers/dom/shared/ReactDOMSelection.js#L44-L58
    let contentEditable;
    try {
      contentEditable =
        anchorNode &&
        (anchorNode.nodeType === window.Node.TEXT_NODE
          ? anchorNode.parentNode.isContentEditable
          : anchorNode.isContentEditable);
    } catch (e) {
      return;
    }

    // sanity checks
    if (
      !contentEditable ||
      !this._container || // need a live editor ref for this to work
      !selectionPosition // no selection
    ) {
      return;
    }
    const { inlineToolbarPos } = this.state;
    const newInlineToolbarPos = {
      top: selectionPosition.top + window.scrollY,
      left: (selectionPosition.left + selectionPosition.right) / 2 + window.scrollX, // problem: this results in an element that is incorrectly positioned by -paddingLeft px in the x axis. WAT.
    };

    if (!_.isEqual(newInlineToolbarPos, inlineToolbarPos)) {
      this.setState({ inlineToolbarPos: newInlineToolbarPos });
    }
  }

  // This method is purely visual - it persists a selection visually so that
  // it can still serve as a UI cue when the editor loses focus (i.e. when the
  // user is editing a link). If you want to prevent the selection from
  // changing, do your thing in onChange...
  freezeSelection = () => {
    const selectionRects = window.getSelection().getRangeAt(0).getClientRects();
    const node = findDOMNode(this._container);
    const editorOffset = node.getBoundingClientRect();
    const frozenSelection = Array.from(selectionRects).map((rect) => ({
      top: rect.top - editorOffset.top,
      left: rect.left - editorOffset.left,
      width: rect.right - rect.left,
      height: rect.bottom - rect.top,
    }));
    this.setState({ frozenSelection });
  };

  unfreezeSelection = () => {
    this.setState({ frozenSelection: null });
  };

  setRef = (editor) => {
    this._editor = editor;
  };

  focusEditor = () => {
    const { editorState } = this.props;
    const hasFocus = editorState.getSelection().getHasFocus();
    if (this._editor && !hasFocus) {
      // this functionality can cause particularly hard-to-diagnose bugs, so
      // I'm keeping the console.log & console.trace around for now
      console.log("focusEditor");
      window.console.trace();
      this._editor.focus();
    }
  };

  focus = () => {
    this._editor.focus();
  };

  blur = () => {
    this._editor.blur();
  };

  exitCurrentMode = () => {
    this._editor.exitCurrentMode();
  };

  onClickContainer = (e) => {
    // don't mess with focus if selection is frozen
    if (this.state.frozenSelection) {
      return;
    }

    // if we've already got focus, ignore
    if (this.props.editorState.getSelection().getHasFocus()) {
      return;
    }

    // ignore if it's an atomic block interaction
    const hasAtomicAncestor = (node) => {
      if (!node) {
        return false;
      }
      if (node.tagName === "FIGURE") {
        return true;
      }
      if (node.classList.contains("DraftEditor-root")) {
        return false;
      }
      return hasAtomicAncestor(node.parentElement);
    };

    // if the click is below the draft editor node, focus the last block
    if (e.clientY > findDOMNode(this._editor).getBoundingClientRect().bottom) {
      this.props.onChange(EditorState.moveFocusToEnd(this.props.editorState), () => this.focusEditor());
      return;
    }

    if (!hasAtomicAncestor(e.target)) {
      this.focusEditor();
    }
  };

  render() {
    const { editorState, onChange, placeholder, className, blockRendererFn, blockRenderMap, blockStyleFn } = this.props;
    const { inlineToolbarPos, frozenSelection } = this.state;

    return (
      <div
        className={cx(styles.RichTextEditor, className)}
        ref={(node) => {
          this._container = node;
        }}
        onClick={this.onClickContainer}
      >
        <CustomDraftEditor
          ref={this.setRef}
          editorState={editorState}
          onChange={onChange}
          placeholder={placeholder}
          blockRendererFn={blockRendererFn}
          blockRenderMap={blockRenderMap}
          blockStyleFn={blockStyleFn}
        />
        <InlineToolbar
          editorState={editorState}
          onChange={onChange}
          position={inlineToolbarPos}
          selectionIsFrozen={!!frozenSelection}
          freezeSelection={this.freezeSelection}
          unfreezeSelection={this.unfreezeSelection}
          restoreFocus={this.focusEditor}
        />
      </div>
    );
  }
}
